// Inline images in an HTML email: <img src="cid:X"> refers to the attachment
// whose Content-ID is X. The browser cannot load a cid: URL, so without this
// the conversation view shows a broken image where the customer's photo was.
//
// The bytes are in blob storage behind the authorized download route, which an
// <img> cannot call (it needs a bearer token). So the view fetches each inline
// image with the token, makes an object URL for it, and this module swaps the
// cid: reference for that URL.
//
// Kept free of React so node --test can cover it (see toAddressAutoSelect.js
// for the same pattern).

// The attachments of one message that can be shown in place of a cid:
// reference: a Content-ID, bytes stored, and an image type.
export function inlineImageAttachments(message) {
  return (message?.attachments ?? []).filter((a) =>
    a && a.contentId && a.available && String(a.contentType ?? '').toLowerCase().startsWith('image/'))
}

// Replaces src="cid:X" with urlByContentId[X] in already-SANITIZED html. It
// runs after DOMPurify on purpose: DOMPurify strips blob: URLs, and the only
// thing inserted here is an object URL this page created itself. A reference
// with no loaded image is left untouched, so it degrades to the broken image it
// was before rather than to nothing.
//
// The match is exact first, then case-insensitive, because some clients change
// the case of a Content-ID between the header and the body. &amp; is decoded,
// because a sanitizer serializes & in an attribute that way.
export function replaceCidImages(html, urlByContentId) {
  if (!html || !urlByContentId) return html
  const keys = Object.keys(urlByContentId)
  if (!keys.length) return html
  const lower = new Map(keys.map((k) => [k.toLowerCase(), urlByContentId[k]]))
  return html.replace(/(\ssrc\s*=\s*)(["'])cid:([^"']+)\2/gi, (whole, pre, quote, rawId) => {
    const id = rawId.replace(/&amp;/g, '&').trim()
    const url = urlByContentId[id] ?? lower.get(id.toLowerCase())
    return url ? `${pre}${quote}${url}${quote}` : whole
  })
}
